import { useCallback, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUserProfileContext } from '@/contexts/UserProfileContext';
import { BrandContext } from '@/hooks/useBrand';
import { brandPreferenceKey, canChooseBrand, isBrand, resolveBrand, type Brand } from '@/lib/brand';
import { supabase } from '@/integrations/supabase/client';
import '@/styles/brand-conecta.css';

export function BrandProvider({ children }: { children: ReactNode }) {
  const { profile, isLoadingProfile, isAuthenticated } = useUserProfileContext();
  const equipeId = profile?.equipe_id;
  const { data: equipe, isPending, isError } = useQuery({
    queryKey: ['brand-equipe', profile?.id, equipeId],
    queryFn: async () => {
      const { data, error } = await supabase.from('equipes')
        .select('id, tipo_pessoa').eq('id', equipeId!).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.ativo && !!equipeId && isAuthenticated,
    staleTime: 30_000,
    retry: 2,
  });
  const canChoose = isAuthenticated && canChooseBrand(profile);
  const isLoadingBrand = isLoadingProfile || (!!profile?.ativo && !!equipeId && isPending && isAuthenticated);
  const storageKey = profile && !isLoadingBrand && !isError
    ? brandPreferenceKey(profile, equipe?.tipo_pessoa)
    : null;
  const [preference, setPreference] = useState<{ key: string; brand: Brand } | null>(null);

  useEffect(() => {
    if (!storageKey || !canChoose) return;
    const readPreference = () => {
      try {
        const stored = localStorage.getItem(storageKey);
        setPreference(isBrand(stored) ? { key: storageKey, brand: stored } : null);
      } catch {
        setPreference(null);
      }
    };
    readPreference();
    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey || event.key === null) readPreference();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey, canChoose]);

  const brand = isAuthenticated && profile?.ativo
    ? resolveBrand(equipe?.tipo_pessoa, canChoose, preference?.key === storageKey ? preference?.brand : null)
    : 'empresarial-prime';

  useLayoutEffect(() => {
    if (brand === 'prime-conecta') document.documentElement.dataset.brand = brand;
    else delete document.documentElement.dataset.brand;
    return () => { delete document.documentElement.dataset.brand; };
  }, [brand]);

  const setBrand = useCallback((next: Brand) => {
    if (!canChoose || !storageKey || !isBrand(next)) return;
    setPreference({ key: storageKey, brand: next });
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      return;
    }
  }, [canChoose, storageKey]);

  const value = useMemo(() => ({
    brand,
    setBrand,
    toggleBrand: () => setBrand(brand === 'prime-conecta' ? 'empresarial-prime' : 'prime-conecta'),
    canChooseBrand: canChoose && !!storageKey,
    isLoadingBrand,
    isPrimeTec: false,
    isEmpresarialPrime: brand === 'empresarial-prime',
    isConecta: brand === 'prime-conecta',
  }), [brand, setBrand, canChoose, storageKey, isLoadingBrand]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}
